import type { ClipboardEvent } from 'react';

import { numberInputSeparators } from './appLocale';

/** Keep dot-decimal clipboard values usable in ungrouped comma-decimal inputs. */
export const normalizeNumberInputPaste = (
  text: string,
  decimalSeparator: string
): string =>
  decimalSeparator !== '.' && /^[+-]?(?:\d+\.\d*|\.\d+)$/.test(text.trim())
    ? text.trim().replace('.', decimalSeparator)
    : text;

/**
 * NumberFormat accepts a typed dot as a decimal alias, but strips it from
 * multi-character paste in comma locales. Insert its localized equivalent
 * through the normal input event so NumberFormat still owns precision,
 * clamping and numeric callbacks. Grouped inputs retain native parsing:
 * their dot may be a thousands separator rather than a decimal alias.
 */
export const handleNumberInputPaste = (
  event: ClipboardEvent<HTMLInputElement>,
  grouped = false,
  decimalSeparator = numberInputSeparators().decimalSeparator
): void => {
  const input = event.currentTarget;
  if (
    grouped ||
    event.defaultPrevented ||
    input.readOnly ||
    input.disabled ||
    input.selectionStart === null ||
    input.selectionEnd === null
  ) {
    return;
  }

  const text = event.clipboardData.getData('text/plain');
  const normalized = normalizeNumberInputPaste(text, decimalSeparator);
  if (normalized === text) return;

  event.preventDefault();
  input.setRangeText(
    normalized,
    input.selectionStart,
    input.selectionEnd,
    'end'
  );
  input.dispatchEvent(new Event('input', { bubbles: true }));
};
